import { motion } from 'framer-motion'
import TeamSection from '../components/sections/TeamSection'
import CTASection from '../components/sections/CTASection'
import StatsStrip from '../components/sections/StatsStrip'
import GlassCard from '../components/ui/GlassCard'
import ScrollReveal from '../components/ui/ScrollReveal'
import Parallax from '../components/ui/Parallax'

const values = [
  {
    title: 'Excellence',
    description:
      'We hold ourselves to the highest standards. Every pixel, every line of code, every interaction is crafted with obsessive attention to quality.',
    icon: '🏆',
  },
  {
    title: 'Innovation',
    description:
      'We stay at the cutting edge of technology and design, constantly exploring new approaches to deliver solutions that stand out.',
    icon: '💡',
  },
  {
    title: 'Transparency',
    description:
      'Clear communication, honest timelines, and open collaboration. Our clients are partners, and we treat them as such.',
    icon: '🔍',
  },
  {
    title: 'Impact',
    description:
      'We measure success by the results we deliver. Every project is designed to drive measurable business outcomes.',
    icon: '📊',
  },
]

const processSteps = [
  {
    step: '01',
    title: 'Discovery',
    description:
      'We dive deep into your business, goals, audience, and competition to build a solid strategic foundation.',
  },
  {
    step: '02',
    title: 'Strategy',
    description:
      'We craft a comprehensive plan covering architecture, design direction, technology stack, and timeline.',
  },
  {
    step: '03',
    title: 'Design',
    description:
      'Our designers bring the vision to life with pixel-perfect mockups, interactive prototypes, and meticulous attention to detail.',
  },
  {
    step: '04',
    title: 'Development',
    description:
      'Our engineers build using modern frameworks and best practices, with continuous integration and regular demos.',
  },
  {
    step: '05',
    title: 'Launch',
    description:
      'We deploy, test, optimize, and monitor. Your site goes live with confidence and a full support plan in place.',
  },
]

export default function About() {
  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gray-950" />
        <Parallax speed={0.15} className="absolute top-0 left-0 w-[400px] h-[400px]">
          <div className="w-full h-full rounded-full bg-electric-blue/10 blur-[120px]" />
        </Parallax>
        <Parallax speed={0.12} className="absolute bottom-0 right-0 w-[400px] h-[400px]">
          <div className="w-full h-full rounded-full bg-electric-purple/10 blur-[120px]" />
        </Parallax>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-4 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-electric-blue bg-electric-blue/10 rounded-full border border-electric-blue/20"
          >
            About Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white"
          >
            We Are{' '}
            <span className="bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent">
              SST WebAgency
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-lg text-gray-400 max-w-2xl mx-auto"
          >
            A team of passionate creators, engineers, and strategists on a
            mission to build digital experiences that make a difference.
          </motion.p>
        </div>
      </section>

      {/* Story */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <GlassCard className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-electric-blue">
                    Our Story
                  </span>
                  <h2 className="text-3xl font-bold text-white mt-3 mb-6">
                    Crafting Digital Excellence Since 2019
                  </h2>
                  <div className="space-y-4 text-gray-400 leading-relaxed">
                    <p>
                      SST WebAgency was born from a simple belief: the digital
                      world deserves better. Better design. Better technology.
                      Better experiences. What started as a two-person studio
                      has grown into a full-service digital agency working with
                      startups and Fortune 500 companies alike.
                    </p>
                    <p>
                      We've delivered over 150 projects across 12 countries,
                      partnering with visionary founders and established
                      enterprises to bring ambitious digital ideas to life.
                      Every project is an opportunity to push boundaries and
                      set new standards.
                    </p>
                    <p>
                      Our team brings together expertise from Google, Spotify,
                      and Figma, combining deep technical knowledge with
                      world-class design thinking. We don't just build
                      websites — we build digital ecosystems that drive growth.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: '150+', label: 'Projects' },
                    { value: '50+', label: 'Happy Clients' },
                    { value: '12+', label: 'Countries' },
                    { value: '5', label: 'Years Strong' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="p-6 rounded-2xl bg-white/5 border border-white/5 text-center"
                    >
                      <div className="text-3xl font-bold bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent">
                        {stat.value}
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </GlassCard>
          </ScrollReveal>
        </div>
      </section>

      <StatsStrip />

      {/* Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block mb-4 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-electric-blue bg-electric-blue/10 rounded-full border border-electric-blue/20">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              What Drives Us
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <ScrollReveal key={value.title} delay={i * 0.1}>
                <GlassCard className="p-6 text-center h-full">
                  <div className="text-4xl mb-4">{value.icon}</div>
                  <h3 className="text-lg font-bold text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {value.description}
                  </p>
                </GlassCard>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block mb-4 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-electric-blue bg-electric-blue/10 rounded-full border border-electric-blue/20">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              How We Work
            </h2>
          </motion.div>

          <div className="relative">
            {/* Connecting line */}
            <div className="hidden lg:block absolute top-24 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-electric-blue/20 via-electric-purple/20 to-transparent" />

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {processSteps.map((step, i) => (
                <ScrollReveal key={step.step} delay={i * 0.1}>
                  <div className="text-center relative">
                    <div className="w-14 h-14 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-electric-blue to-electric-purple flex items-center justify-center text-white font-bold text-lg relative z-10">
                      {step.step}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TeamSection />
      <CTASection />
    </main>
  )
}
